import { useState, useEffect, useCallback } from "react";

/**
 * usePersistedTab
 * Persists the active tab key to localStorage so it survives page refreshes.
 *
 * @param {string} storageKey  - unique localStorage key, e.g. "tab_catalogue"
 * @param {string} defaultTab  - fallback value if nothing is stored
 * @returns {[string, Function]} [activeTab, setActiveTab]
 */
const usePersistedTab = (storageKey, defaultTab) => {
    const [activeTab, setActiveTabState] = useState(() => {
        try {
            return localStorage.getItem(storageKey) || defaultTab;
        } catch {
            return defaultTab;
        }
    });

    const setActiveTab = useCallback((tab) => {
        try {
            localStorage.setItem(storageKey, tab);
        } catch { /* ignore */ }
        setActiveTabState(tab);
    }, [storageKey]);

    // Keep storage in sync if key changes (e.g. hot-reload)
    useEffect(() => {
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored && stored !== activeTab) setActiveTabState(stored);
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    return [activeTab, setActiveTab];
};

export default usePersistedTab;

